import { lazy, Suspense } from "react";
import { Navigate, useRoutes } from "react-router-dom";

const Home = lazy(() => import("../pages/home/Home"));
const CreateEventCard = lazy(() => import("../pages/home/CreateEventCard"));
const RequestService = lazy(() => import("../pages/home/RequestService"));
const SportCard = lazy(() => import("../pages/cards/SportCard"));
const ClubCard = lazy(() => import("../pages/cards/ClubCard"));
const CrewCard = lazy(() => import("../pages/cards/CrewCard"));
const Community = lazy(() => import("../pages/community/Community"));
const ChatScreen = lazy(() => import("../pages/community/ChatScreen"));
const LoginPage = lazy(() => import("../component/auth/Login"));
const Signup = lazy(() => import("../component/auth/Signup"));
const ForgotPassword = lazy(() => import("../component/auth/ForgotPassword"));
const PlayerDescription = lazy(() =>
  import("../pages/community/PlayerDescription")
);
const AuthLayout = lazy(() => import("../layout/authLayout"));
const DashboardLayout = lazy(() => import("../layout/dashboardLayout"));
const ProtectedRoute = lazy(() => import("../authService/ProtectedRoute"));
const ResetPassword = lazy(() => import("../component/auth/ResetPassword"));
const Rating = lazy(() => import("../pages/profile/rating"));
const VenueOperations = lazy(() => import("pages/profile/venueOperations"));
const EditProfile = lazy(() => import("pages/profile/profileEdit"));
const BookingHistory = lazy(() => import("pages/profile/bookingHistory"));
const PurchaseDetail = lazy(() =>
  import("pages/profile/bookingHistory/purchaseDetail")
);
const ChangePassword = lazy(() => import("component/auth/ChangePassword"));
const ArenaDetails = lazy(() =>
  import("pages/profile/venueOperations/arenaDetails")
);
const PaymentMethods = lazy(() => import("pages/profile/paymentMethods"));
const PaymentHistory = lazy(() =>
  import("pages/profile/paymentMethods/paymentHistory")
);
const PaymentHistoryDetail = lazy(() =>
  import("pages/profile/paymentMethods/paymentHistoryDetail")
);
const AddAccount = lazy(() => import("pages/profile/paymentMethods/addAccount"));
const Messages = lazy(() => import("pages/messages"));
const MyPosting = lazy(() => import("pages/myPosting"));
const CardDetails = lazy(() => import("pages/myPosting/cardDetails"));
const GameOn = lazy(() => import("pages/home/GameOn"));
const ClubMatch = lazy(() => import("pages/home/ClubMatch"));
const ClubVenue = lazy(() => import("pages/home/ClubVenue"));
const VenueCard = lazy(() => import("pages/cards/VenueCard"));
const AssignMatch = lazy(() => import("pages/myPosting/assignMatch"));
const RequestSent = lazy(() => import("pages/myPosting/requestSent"));
const PaymentSuccess = lazy(() =>
  import("pages/home/processPayment/paymentSuccessPage")
);
const DeepLinkLanding = lazy(() => import("pages/deepLink/DeepLinkLanding"));
const ScorecardSpectator = lazy(() =>
  import("pages/scorecard/ScorecardSpectator")
);
const SportPassportPage = lazy(() =>
  import("pages/sportPassport/SportPassportPage")
);

function RouteLoader() {
  return (
    <div className="min-h-screen bg-gray-100 px-4 py-10">
      <div className="mx-auto w-full max-w-3xl animate-pulse space-y-4">
        <div className="h-10 w-48 rounded-xl bg-gray-200" />
        <div className="h-28 rounded-2xl bg-gray-200" />
        <div className="h-28 rounded-2xl bg-gray-200" />
      </div>
    </div>
  );
}

export default function Router() {
  const routes = useRoutes([
    {
      path: "/game/:gameId",
      element: <DeepLinkLanding type="game" />,
    },
    {
      path: "/event/:eventId",
      element: <DeepLinkLanding type="event" />,
    },
    {
      path: "/club-invite/:inviteToken",
      element: <DeepLinkLanding type="club-invite" />,
    },
    {
      path: "/scorecard/spectator/:spectatorToken",
      element: <ScorecardSpectator />,
    },
    {
      path: "/scorecard/scorer/:scorerToken",
      element: <DeepLinkLanding type="scorecard-scorer" />,
    },
    {
      path: "/p/:publicProfileId/:sportId",
      element: <SportPassportPage />,
    },
    {
      path: "/p/:publicProfileId",
      element: <SportPassportPage />,
    },
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <DashboardLayout />{" "}
        </ProtectedRoute>
      ),
      children: [
        { element: <Navigate to="/dashboard" />, index: true },
        { path: "/dashboard", element: <Home /> },
        { path: "/club/game-on", element: <GameOn /> },
        { path: "/club/game-on/:id", element: <GameOn /> },
        // { path: "/club/service", element: <ClubService /> },
        // { path: "/event/request-service", element: <ClubService /> },
        { path: "/club/match/:id", element: <ClubMatch /> },
        { path: "/club/venue", element: <ClubVenue /> },
        { path: "/payment-success", element: <PaymentSuccess /> },
        { path: "/cards/sport", element: <SportCard /> },
        { path: "/cards/crew", element: <CrewCard /> },
        { path: "/cards/club", element: <ClubCard /> },
        { path: "/cards/venue", element: <VenueCard /> },
        { path: "/event/card/:id", element: <CreateEventCard /> },
        { path: "/event/request-service/:id", element: <RequestService /> },
        // { path: "/cards/player", element: <PlayerDetail /> },
        // { path: "/cards/crew", element: <CrewDetail /> },
        // { path: "/cards/club", element: <ClubDetail /> },
        { path: "/community", element: <Community /> },
        { path: "/messages", element: <Messages /> },
        { path: "/my-posting", element: <MyPosting /> },
        { path: "/chat/:id", element: <ChatScreen /> },
        { path: "/rating", element: <Rating /> },
        { path: "/booking-history", element: <BookingHistory /> },
        { path: "/booking-history/:id", element: <PurchaseDetail /> },
        { path: "/venue-operations", element: <VenueOperations /> },
        { path: "/profile-edit", element: <EditProfile /> },
        { path: "/payment-methods", element: <PaymentMethods /> },
        { path: "/payment-history", element: <PaymentHistory /> },
        {
          path: "/payment-history/:type/:id/details",
          element: <PaymentHistoryDetail />,
        },
        { path: "/payment-methods/addAccount", element: <AddAccount /> },
        { path: "/card-details/:type/:id", element: <CardDetails /> },

        {
          path: "/player-details/:id",
          element: <PlayerDescription />,
        },
        { path: "/arena-details/:venueId/:id", element: <ArenaDetails /> },
        { path: "/assign-match", element: <AssignMatch /> },
        { path: "/request-sent", element: <RequestSent /> },
      ],
    },
    {
      path: "/",
      element: <AuthLayout />,
      children: [
        { path: "login", element: <LoginPage /> },
        { path: "signup", element: <Signup /> },
        { path: "forgot-password", element: <ForgotPassword /> },
        { path: "resetPassword", element: <ResetPassword /> },
        { path: "change-password", element: <ChangePassword /> },
      ],
    },
    {
      path: "*",
      element: <DeepLinkLanding type="unknown" />,
    },
    // { path: "*", element: isLogin ? <Page404 /> : <Navigate to="/login" /> }
  ]);

  return <Suspense fallback={<RouteLoader />}>{routes}</Suspense>;
}
